import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          params.append(key, String(value));
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9k39v6c26n1c7c5qbq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        const msg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
        setStatus('error');
        setErrorMsg(msg);
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查网络后重试');
    }
  };

  return (
    <section id="contact" className="w-full bg-background-100 py-14 md:py-20">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
            联系我们
          </h2>
          <p className="mt-3 text-sm text-foreground-500 max-w-[480px] mx-auto">
            投稿、合作、反馈 — 妖精动漫期待你的声音
          </p>
        </div>

        <div className="max-w-[600px] mx-auto">
          {status === 'success' ? (
            <div className="text-center py-10 bg-background-50 border border-background-200/70 rounded-xl">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
                <i className="ri-check-line text-2xl text-accent-500"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-800 mb-2">提交成功！</h3>
              <p className="text-sm text-foreground-500">感谢你的来信，我们会尽快回复。</p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-4 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                继续提交
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="contact_name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="你的名字"
                  />
                </div>
                <div>
                  <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="contact_email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact_subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                  placeholder="请选择：投稿 / 合作 / 反馈"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact_message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                  placeholder="请详细描述你的需求或建议..."
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500字</p>
              </div>

              <div className="honeypot-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {status === 'error' && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{errorMsg}</p>
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-white text-sm font-semibold rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                提交留言
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}